export const FLAGGED_CASES_SOCKET_PATH = "/ws/flagged-cases/";

export const isFlaggedCaseCount = (count) => Number.isSafeInteger(count) && count >= 0;

// One connection belongs to one signed-in user. Dependencies are injected so
// ticket races, reconnects, and late responses can be tested without a server.
export function createFlaggedCaseCountConnection({
  userId,
  requestCount,
  requestSocketURL,
  createSocket,
  onCount,
  canReconnect,
  onOpen = () => {},
  isCurrentSession = () => true,
  setTimer = setTimeout,
  clearTimer = clearTimeout,
}) {
  let disposed = false;
  let socket = null;
  let reconnectTimer = null;
  let failures = 0;
  let revision = 0;
  const isActive = () => !disposed && isCurrentSession();

  const applyCount = (count, expectedRevision = revision) => {
    if (!isActive() || expectedRevision !== revision || !isFlaggedCaseCount(count)) return false;
    revision += 1;
    onCount(count);
    return true;
  };

  const scheduleReconnect = () => {
    if (!isActive() || reconnectTimer !== null) return;
    const delay = Math.min(3000 * ++failures, 30000);
    reconnectTimer = setTimer(() => {
      reconnectTimer = null;
      open();
    }, delay);
  };

  const open = async () => {
    if (!isActive()) return;
    try {
      const url = await requestSocketURL();
      if (!isActive()) return;
      const ws = createSocket(url);
      socket = ws;
      ws.onopen = () => {
        if (!isActive() || socket !== ws) return;
        failures = 0;
        onOpen();
      };
      ws.onmessage = (event) => {
        if (!isActive() || socket !== ws) return;
        try {
          const data = JSON.parse(event.data);
          if (data?.type === "flagged_case_count" && String(data.user_id) === String(userId)) {
            applyCount(data.count);
          }
        } catch {
          // A malformed message must not clear a valid count or end the session.
        }
      };
      ws.onclose = (event) => {
        if (!isActive() || socket !== ws) return;
        socket = null;
        // Unlike other firm sockets, 4403 here means an unsupported login.
        if (event.code === 4403 || !canReconnect(event)) return;
        scheduleReconnect();
      };
      ws.onerror = () => {
        if (isActive() && socket === ws) ws.close();
      };
    } catch (error) {
      if (!isActive()) return;
      // The ticket helper already handles authentication/session failures.
      if ([401, 403, 404].includes(error?.response?.status)) return;
      scheduleReconnect();
    }
  };

  const startingRevision = revision;
  Promise.resolve()
    .then(() => (isActive() ? requestCount() : undefined))
    .then((count) => {
      // Socket messages and mutations take precedence over an older bootstrap.
      if (revision === startingRevision) applyCount(count);
    })
    .catch(() => {
      // The socket sends the authoritative count on every successful connect.
    });
  open();

  return {
    applyCount,
    getRevision: () => revision,
    dispose() {
      disposed = true;
      if (reconnectTimer !== null) clearTimer(reconnectTimer);
      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onclose = null;
        socket.onerror = null;
        socket.close();
        socket = null;
      }
    },
  };
}
